import type { TipTapDoc } from "@compass/shared/schema/tiptapDoc";
import React, { useEffect, useRef, useState } from "react";
import { TipTapRenderer } from "@compass/shared/components/TipTapRenderer";

const PROSE_WRAPPER_CLASS = `prose prose-sm max-w-none whitespace-pre-wrap text-[#424242] **:wrap-anywhere [&_.indent-level-1]:ml-5 [&_.indent-level-2]:ml-10 [&_.indent-level-3]:ml-15 [&_.indent-level-4]:ml-20 [&_.indent-level-5]:ml-25`;

interface ProseDocumentProps {
    doc: TipTapDoc;
    className?: string;
    /** When true, renders full content without truncation or "Show more". */
    expanded?: boolean;
    onShowMore?: () => void;
}

export const ProseDocument = ({ doc, className, expanded, onShowMore }: ProseDocumentProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const maxHeight = 200;

    const showFull = expanded || isExpanded;

    useEffect(() => {
        if (expanded) return;
        const el = contentRef.current;
        if (!el) return;
        // Set in both directions: when the doc shrinks below the threshold the
        // toggle should disappear, not stay stale at `true`.
        setIsOverflowing(el.scrollHeight > maxHeight);
    }, [doc, expanded]);

    return (
        <div className="w-full min-w-0">
            <div
                ref={contentRef}
                style={{
                    maxHeight: showFull ? "none" : maxHeight,
                    overflow: showFull ? "visible" : "hidden",
                    position: "relative",
                    transition: expanded ? undefined : "max-height 0.3s ease",
                    overflowWrap: "anywhere",
                }}
                className={`${PROSE_WRAPPER_CLASS} ${className ?? ""}`}
            >
                <TipTapRenderer doc={doc} />
            </div>

            {/* Render the toggle whenever truncation would apply, so users can
             *  collapse back after expanding. (Hiding it once `showFull=true`
             *  made "Show less" unreachable.) */}
            {isOverflowing && !expanded && (
                <button
                    type="button"
                    onClick={() => (onShowMore ? onShowMore() : setIsExpanded((prev) => !prev))}
                    className="text-current underline"
                    style={{
                        marginTop: 10,
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        fontSize: "0.875rem",
                    }}
                >
                    {isExpanded ? "Show less" : "Show more"}
                </button>
            )}
        </div>
    );
};
